/**
 * Terminliste und das Ansetzen einer neuen Sitzung.
 */
import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api, ApiFehler } from '../api/client.js'
import { formatDatumZeit } from '../api/format.js'
import { useLaden } from '../api/laden.js'
import type { EventTyp, SitzungsEvent } from '../api/typen.js'
import {
  Abschnitt,
  Amtsknopf,
  eingabeKlasse,
  Fehlermeldung,
  Feld,
  Laufvermerk,
  LeererZustand,
} from '../bausteine/Bausteine.js'
import { KalenderAbo } from '../bausteine/Kalender.js'

export function Termine() {
  const events = useLaden(() => api.get<SitzungsEvent[]>('/api/events'))
  if (events.laedt) return <Laufvermerk />

  const jetzt = Date.now()
  const offene = (events.daten ?? []).filter((e) => e.status !== 'abgeschlossen')
  const vergangene = (events.daten ?? []).filter((e) => e.status === 'abgeschlossen')

  const zeile = (e: SitzungsEvent) => (
    <li key={e.id}>
      <Link
        to={`/termine/${e.id}`}
        className="flex items-center justify-between gap-3 border border-eiche bg-bank px-4 py-3 transition-colors hover:border-messing"
      >
        <span className="min-w-0">
          <span className="block truncate text-lampe">{e.title}</span>
          <span className="protokoll normal-case">
            {formatDatumZeit(e.startsAt)} · {e.location}
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-2">
          {e.type === 'sonderschnapsevent' && <span className="stempel stempel-vollstreckt">Sonder</span>}
          {e.status === 'laufend' && Date.parse(e.endsAt) > jetzt && (
            <span className="stempel stempel-offen">Läuft</span>
          )}
        </span>
      </Link>
    </li>
  )

  return (
    <>
      <Abschnitt
        titel="Angesetzte Termine"
        aktion={
          <Link to="/termine/neu" className="protokoll normal-case text-messing hover:text-lampe">
            + Ansetzen
          </Link>
        }
      >
        {offene.length === 0 ? (
          <LeererZustand text="Kein Termin angesetzt. Die Kammer wartet auf Anträge." />
        ) : (
          <ul className="flex flex-col gap-2">{offene.map(zeile)}</ul>
        )}
      </Abschnitt>

      <Abschnitt titel="Im eigenen Kalender">
        <KalenderAbo />
      </Abschnitt>

      {vergangene.length > 0 && (
        <Abschnitt titel="Geschlossene Sitzungen">
          <ul className="flex flex-col gap-2">{vergangene.map(zeile)}</ul>
        </Abschnitt>
      )}
    </>
  )
}

/** Lokale Datumseingabe → Unix-ms. `datetime-local` liefert Ortszeit des Geräts. */
function alsMs(wert: string): number {
  return new Date(wert).getTime()
}

export function TerminAnsetzen() {
  const navigiere = useNavigate()
  const [titel, setTitel] = useState('')
  const [ort, setOrt] = useState('')
  const [beginn, setBeginn] = useState('')
  const [ende, setEnde] = useState('')
  const [typ, setTyp] = useState<EventTyp>('regulaer')
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  async function ansetzen(e: FormEvent) {
    e.preventDefault()
    setLaeuft(true)
    setFehler(null)
    try {
      const event = await api.post<SitzungsEvent>('/api/events', {
        title: titel,
        location: ort,
        startsAtMs: alsMs(beginn),
        endsAtMs: alsMs(ende),
        type: typ,
      })
      navigiere(`/termine/${event.id}`)
    } catch (f) {
      setFehler(f instanceof ApiFehler ? f.message : 'Der Termin konnte nicht angesetzt werden.')
    } finally {
      setLaeuft(false)
    }
  }

  const vollstaendig = titel.trim() && ort.trim() && beginn && ende

  return (
    <>
      <section className="pt-8">
        <h1 className="text-2xl">Termin ansetzen</h1>
        <p className="mt-2 text-sm text-akte/75">
          Die Kammer beraumt eine Sitzung an. Ein Sonderschnapsevent braucht 24 Stunden Vorlauf —
          sonst weist die Kammer den Antrag zurück (§4).
        </p>
      </section>

      <form onSubmit={ansetzen} className="mt-6 flex flex-col gap-4">
        <Feld label="Bezeichnung der Sache">
          <input
            value={titel}
            onChange={(e) => setTitel(e.target.value)}
            className={`w-full ${eingabeKlasse}`}
            placeholder="Donnerstagsrunde"
            maxLength={120}
          />
        </Feld>
        <Feld label="Ort">
          <input
            value={ort}
            onChange={(e) => setOrt(e.target.value)}
            className={`w-full ${eingabeKlasse}`}
            placeholder="Squashhalle Nord"
            maxLength={160}
          />
        </Feld>
        <div className="grid grid-cols-2 gap-3">
          <Feld label="Beginn">
            <input
              type="datetime-local"
              value={beginn}
              onChange={(e) => setBeginn(e.target.value)}
              className={`w-full ${eingabeKlasse}`}
            />
          </Feld>
          <Feld label="Ende">
            <input
              type="datetime-local"
              value={ende}
              onChange={(e) => setEnde(e.target.value)}
              className={`w-full ${eingabeKlasse}`}
            />
          </Feld>
        </div>

        <fieldset>
          <legend className="protokoll">Art der Sitzung</legend>
          <div className="mt-2 grid grid-cols-1 gap-2">
            {(
              [
                ['regulaer', 'Reguläres Event', 'Strafschnaps ab 30 min Verspätung (§1).'],
                [
                  'sonderschnapsevent',
                  'Sonderschnapsevent',
                  'Erste Einheit ab 15 min, Gesamtstrafe verdoppelt (§4). 24 h Vorlauf nötig.',
                ],
              ] as const
            ).map(([wert, label, hinweis]) => (
              <label
                key={wert}
                className={`flex cursor-pointer items-start gap-3 border px-4 py-3 transition-colors ${
                  typ === wert ? 'border-messing bg-bank' : 'border-eiche bg-bank/60 hover:border-messing/50'
                }`}
              >
                <input
                  type="radio"
                  name="typ"
                  value={wert}
                  checked={typ === wert}
                  onChange={() => setTyp(wert)}
                  className="mt-1 accent-[#c89b3c]"
                />
                <span>
                  <span className="block text-lampe">{label}</span>
                  <span className="block text-xs text-akte/70">{hinweis}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <Amtsknopf type="submit" disabled={!vollstaendig || laeuft}>
          {laeuft ? 'Wird angesetzt …' : 'Termin ansetzen'}
        </Amtsknopf>
        <Fehlermeldung text={fehler} />
      </form>
    </>
  )
}
